import { useProjectId } from 'domain/shared/project';
import { PropsWithChildren, useMemo } from 'react';
import { useGlobalSecurityReactContext, PermissionsMap, SecurityContextTemplate } from 'framework/authorization';
import { NEW_PROJECT_CONTEXT } from '../NewProjectContext';
import { useProjectDetails } from '../dashboard/hooks/useProjectDashboard';
import { Loading } from 'framework/ui/mui-builder';
import { omitDeep } from '@apollo/client/utilities/internal';
import { ProjectSecurityContext, ProjectDataContext, useNewProjectService } from 'domain/shared/newProject';

export const ProjectContext = ({ children }: PropsWithChildren) => {
  const id = useProjectId();
  const { project, refetch, isDataReloading } = useProjectDetails(id);
  const globalContext = useGlobalSecurityReactContext() || {};
  const { getProjectEntityPermissions } = useNewProjectService();

  const { contextValue, projectPermissionsInGlobalFormat, projectPermissions } = useMemo(() => {
    if (!project) {
      return {
        contextValue: null,
        projectPermissionsInGlobalFormat: [],
      };
    }

    const permissionsGlobalFormat =
      project?.accessInfo?.sections.flatMap(({ section, allowedActions }) =>
        allowedActions.map(permission => `GRAPHQL__${section}__${permission}`),
      ) || [];

    return {
      contextValue: { data: omitDeep(project, '__typename'), refetch },
      projectPermissionsInGlobalFormat: permissionsGlobalFormat,
      projectPermissions: project?.accessInfo,
    };
    // TODO: Remove omitDeep after backend update
  }, [project, refetch]);

  if (isDataReloading) {
    return <Loading />;
  }

  return (
    <SecurityContextTemplate
      id={id}
      getPermissionsFunc={projectId =>
        Promise.all([
          Promise.resolve(Object.keys(globalContext.permissions as unknown as PermissionsMap) || []),
          Promise.resolve(projectPermissionsInGlobalFormat || []),
          getProjectEntityPermissions('Project', projectId).catch(e => {
            return [];
          }),
        ]).then(permissions => {
          return permissions.reduce((flatten, arr) => [...flatten, ...arr]);
        })
      }
      contextName={NEW_PROJECT_CONTEXT}
    >
      <ProjectDataContext.Provider value={contextValue}>
        <ProjectSecurityContext permissions={projectPermissions}>{children}</ProjectSecurityContext>
      </ProjectDataContext.Provider>
    </SecurityContextTemplate>
  );
};
